import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {MediaPlanApi} from '../../../api/mediaPlanApi';

// Preview de la celda citada por la propuesta: al pasar el mouse sobre un link de evidencia
// ("C5 ↗"), muestra un pedacito de la planilla alrededor de esa celda, con ella resaltada. Responde
// "¿de dónde salió este número?" sin sacar al usuario de Dashbo — el link sigue estando para ir a
// Google Sheets.
//
// Se reconstruye desde el GRID ANOTADO del snapshot, no de la planilla en vivo: es lo que la IA
// efectivamente leyó al armar la propuesta. Si alguien editó la planilla después, un preview "en
// vivo" mostraría otro número y parecería un bug; esto es la evidencia auditable.
//
// ⚠️ ANCLAJE: el popover es `position:absolute` dentro del wrapper (patrón `warning-tooltip`, sin
// portal). Envolver un elemento que esté dentro de un contenedor con `overflow:hidden` lo RECORTA
// —le pasa a `.mp-goal-option`—, así que el wrapper va SIEMPRE por fuera de ese contenedor.
const NS = 'media_plan.proposal.cell_peek';
// El hover tiene que ser intencional: sin delay, barrer el mouse por la fila dispara N fetches.
const HOVER_DELAY_MS = 250;
// El popover es NAVEGABLE (una planilla ancha se scrollea), así que salir del trigger no lo cierra
// en el acto: hay una gracia para llegar hasta él. Sin esto, mover el mouse hacia el preview lo
// hacía desaparecer justo cuando el usuario iba a leerlo. El puente invisible del SCSS
// (`.mp-cellpeek-pop::after`) cubre además el hueco visual entre trigger y popover.
const CLOSE_DELAY_MS = 220;
// El snapshot es INMUTABLE (cada relectura con contenido nuevo crea otro), así que un excerpt no
// puede quedar viejo: se cachea para toda la vida de la pantalla. Solo se guardan los éxitos — un
// blip de red no debe dejar una celda rota hasta el F5.
//
// Con COTA: la corrección no la necesita, pero sin techo el Map crece toda la sesión (una revisión
// larga de propuestas puede pasar por cientos de celdas). El tope está muy por encima de lo que
// tiene cualquier propuesta real, así que en la práctica nunca desaloja; solo acota el peor caso.
export const CACHE_MAX_ENTRIES = 200;
const _cache = new Map();

// LRU apoyado en el orden de inserción del Map: leer re-inserta (pasa a ser la más reciente) y al
// desbordar se descarta la primera clave, que es la menos usada recientemente.
function cacheGet(key) {
    if (!_cache.has(key)) return null;
    const value = _cache.get(key);
    _cache.delete(key);
    _cache.set(key, value);
    return value;
}

function cacheSet(key, value) {
    _cache.set(key, value);
    if (_cache.size > CACHE_MAX_ENTRIES) {
        _cache.delete(_cache.keys().next().value);
    }
}

// ¿El valor se ve como un número? Solo para alinearlo a la derecha, como en la planilla. Acepta lo
// que Sheets ya formateó: separadores, moneda, %, negativos y paréntesis contables.
function looksNumeric(value) {
    return /^[-+(]?\s*[$€£¥]?\s*\d[\d.,\s]*\s*[%)]?$/.test(String(value || '').trim());
}

function cellStyle(entry, styles) {
    const style = (entry && entry.s && styles[entry.s]) || null;
    if (!style) return undefined;
    const css = {};
    if (style.bold) css.fontWeight = 700;
    if (style.bg) css.backgroundColor = style.bg;
    // El tamaño de la planilla es relativo al del preview (que ya es chico): se escala contra el
    // 10pt por defecto de Sheets en vez de copiarse literal.
    if (style.font_size) css.fontSize = `${Math.max(0.8, Math.min(1.4, style.font_size / 10))}em`;
    return css;
}

export function CellPeek({agencyClientId, snapshotId, cell, className = '', hint = true, children}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState('idle');       // idle | loading | ready | error
    const [excerpt, setExcerpt] = useState(null);
    const timerRef = useRef(null);          // apertura diferida (hover intencional)
    const closeTimerRef = useRef(null);     // cierre diferido (gracia para llegar al popover)
    const openRef = useRef(false);          // el estado sin esperar al re-render
    const mountedRef = useRef(true);

    const enabled = !!(agencyClientId && snapshotId && cell);

    useEffect(() => () => {
        mountedRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    }, []);

    // Si el trigger pasa a citar OTRA celda (la propuesta se regeneró con el mouse encima), lo que
    // está en pantalla dejó de corresponder: se cierra y se olvida el excerpt viejo. Mostrar el
    // vecindario de la celda anterior bajo un link que ahora dice otra cosa es peor que no mostrar.
    useEffect(() => {
        openRef.current = false;
        setOpen(false);
        setState('idle');
        setExcerpt(null);
    }, [snapshotId, cell]);

    const show = useCallback(() => {
        if (!enabled) return;
        // Volver a entrar cancela el cierre en curso: es el gesto de "voy a leerlo".
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        if (openRef.current || timerRef.current) return;
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (!mountedRef.current) return;
            openRef.current = true;
            setOpen(true);
            const key = `${snapshotId}|${cell}`;
            const cached = cacheGet(key);
            if (cached) {
                setExcerpt(cached);
                setState('ready');
                return;
            }
            setState('loading');
            // `APIClient` NO rechaza en 4xx: resuelve con `{status:'ERROR'}`. La señal de éxito es
            // que venga `excerpt`, no que la promesa no falle.
            MediaPlanApi.cellExcerpt(agencyClientId, snapshotId, cell)
                .then((data) => {
                    if (!mountedRef.current) return;
                    if (data && data.excerpt) {
                        cacheSet(key, data.excerpt);
                        setExcerpt(data.excerpt);
                        setState('ready');
                    } else {
                        setState('error');
                    }
                })
                .catch(() => mountedRef.current && setState('error'));
        }, HOVER_DELAY_MS);
    }, [enabled, agencyClientId, snapshotId, cell]);

    const hide = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (!openRef.current || closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            if (!mountedRef.current) return;
            openRef.current = false;
            setOpen(false);
        }, CLOSE_DELAY_MS);
    }, []);

    // Escape cierra sin esperar la gracia: el popover ahora recibe el mouse, así que puede quedar
    // tapando lo que el usuario quiere leer.
    const closeNow = useCallback((event) => {
        if (event.key !== 'Escape') return;
        if (timerRef.current) clearTimeout(timerRef.current);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        timerRef.current = closeTimerRef.current = null;
        openRef.current = false;
        setOpen(false);
    }, []);

    // Sin datos suficientes (evidencia sin celda, propuesta sin snapshot) el trigger queda tal cual:
    // el link a la planilla sigue funcionando, simplemente no hay preview.
    if (!enabled) return children;

    return (
        <span className={`mp-cellpeek ${className}${open ? ' -open' : ''}`}
              onMouseEnter={show} onMouseLeave={hide}
              onFocus={show} onBlur={hide} onKeyDown={closeNow}>
            {children}
            {/* Que el preview EXISTA tiene que verse: sin una marca, nadie descubre que dejando el
                mouse encima aparece la planilla. El ojo es la señal (se enciende con el hover), no un
                control: el área sensible sigue siendo el trigger entero. */}
            {hint && (
                // `role="img"`: un `aria-label` sobre un `<span>` pelado la mayoría de los lectores
                // de pantalla no lo anuncia. Y sí tiene que anunciarse — para quien navega con
                // teclado es el único indicio de que enfocar acá abre una vista previa.
                <span className="mp-cellpeek-hint" role="img" aria-label={t(`${NS}.hint`)}>
                    <FaIcon iconName="eye"/>
                </span>
            )}
            {open && (
                <div className="mp-cellpeek-pop" role="tooltip">
                    {state === 'loading' && (
                        <div className="mp-cellpeek-loading"><Spinner color="positive small"/></div>
                    )}
                    {state === 'error' && (
                        <div className="mp-cellpeek-msg">{t(`${NS}.error`)}</div>
                    )}
                    {state === 'ready' && excerpt && (
                        <>
                            <div className="mp-cellpeek-head">
                                {t(`${NS}.title`, {
                                    tab: excerpt.tab_title, cell: excerpt.target,
                                    interpolation: {escapeValue: false},
                                })}
                            </div>
                            <div className="mp-cellpeek-scroll">
                                <table className="mp-cellpeek-grid">
                                    <thead>
                                        <tr>
                                            <th className="mp-cellpeek-corner"/>
                                            {excerpt.cols.map((col) => (
                                                <th key={col} className="mp-cellpeek-colhead">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excerpt.rows.map((row) => (
                                            <tr key={row}>
                                                <th className="mp-cellpeek-rowhead">{row}</th>
                                                {excerpt.cols.map((col) => {
                                                    const a1 = `${col}${row}`;
                                                    const entry = excerpt.cells[a1];
                                                    const value = (entry && entry.v) || '';
                                                    return (
                                                        <td key={a1}
                                                            className={`mp-cellpeek-cell${
                                                                a1 === excerpt.target ? ' -target' : ''}${
                                                                looksNumeric(value) ? ' -num' : ''}`}
                                                            style={cellStyle(entry, excerpt.styles || {})}>
                                                            {value}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {excerpt.target_missing && (
                                <div className="mp-cellpeek-msg">{t(`${NS}.cell_empty`)}</div>
                            )}
                            <div className="mp-cellpeek-foot">{t(`${NS}.foot`)}</div>
                        </>
                    )}
                </div>
            )}
        </span>
    );
}

export default CellPeek;
